import './stockCard.scss';

import React from 'react';
import { PropTypes } from 'prop-types';
import classNames from 'classnames';
import { withRouter, Link } from 'react-router-dom';
import moment from 'moment';
import { i18n } from 'core/i18n';

import { formatAmount } from 'utilities/currencyHelper';
import DateHelper from 'utilities/dateHelper';
import { recordLinkV2 } from 'utilities/tagging';
import {
    BROKER_SMALL_FLAG_SIZE,
    DATE_FORMAT_DAY_MONTH_SHORT,
    DATE_FORMAT_SHORT_TIME_24,
    YES,
} from 'constants/index';
import { CLICK_LINK_ACTION, IA_INVESTMENT_HOME_INTERACTIONS } from 'constants/tagging';

import Flag from 'components/broker/commons/flag/flag';
import { OKButton } from 'components/commons/OKButton/OKButton';
import PercentagePill from '../percentagePill/percentagePill';

const { formatText } = i18n;

const StockCard = ({
    stockData: {
        isin,
        description,
        marketLocationDto: { country, micCode, city } = {},
        currency,
        realTimeValue,
        lastTimeUpdatedAt,
        expectedValue,
        marketable,
    } = {},
    compact,
    history,
    index,
    sellLink = '',
}) => {
    const buyLink = `/myprofile/broker/stocksBuy/${isin}/${micCode}`;
    const date = moment(lastTimeUpdatedAt);
    const formattedDate = moment().isSame(date, 'day')
        ? formatText('common-today')
        : DateHelper.buildFormattedDate(date, DATE_FORMAT_DAY_MONTH_SHORT);
    const formattedTime = DateHelper.buildFormattedDate(date, DATE_FORMAT_SHORT_TIME_24);
    const predictedValue = (expectedValue * realTimeValue) / 100 + realTimeValue;

    const className = classNames('brokerIA-stockCard', {
        'brokerIA-stockCard--compact': !!compact,
    });

    const handleSellButton = () => {
        history.push(sellLink);

        recordLinkV2({
            accionEvento: 'sellStock',
            categoriaEvento: IA_INVESTMENT_HOME_INTERACTIONS,
            etiquetaEvento: description,
            nombreProducto: isin,

            // Enhanced Ecommerce payload
            product_id: [isin],
            product_list: ['iainvestment'],
            product_name: [description],
            product_position: [index.toString()],
            tealium_event: 'product_click',
        });
    };

    const handleBuyButton = () => {
        history.push(buyLink);

        recordLinkV2({
            accionEvento: 'buyStock',
            categoriaEvento: IA_INVESTMENT_HOME_INTERACTIONS,
            etiquetaEvento: description,
            nombreProducto: isin,

            // Enhanced Ecommerce payload
            product_id: [isin],
            product_list: ['iainvestment'],
            product_name: [description],
            product_position: [index.toString()],
            tealium_event: 'product_click',
        });
    };

    const doRecordProductLinkClick = () => {
        recordLinkV2({
            accionEvento: CLICK_LINK_ACTION,
            categoriaEvento: IA_INVESTMENT_HOME_INTERACTIONS,
            etiquetaEvento: description,
            productName: description,
        });
    };

    return (
        <div className={className}>
            <div className="brokerIA-stockCard__description">
                <Link
                    className="brokerIA-stockCard__link"
                    onClick={doRecordProductLinkClick}
                    to={`/myprofile/broker/productFile/${isin}/${micCode}`}
                >
                    {description}
                </Link>
                <div className="brokerIA-stockCard__second-line">
                    <Flag
                        className="brokerIA-stockCard__flag"
                        countryCode={country}
                        pixelsSize={BROKER_SMALL_FLAG_SIZE}
                    />
                    <span>{`${city}, ${country}`}</span>
                    {!compact && (
                        <span>{` | ${formatText('brokerIA-stockCard_ISIN')}${isin}`}</span>
                    )}
                </div>
            </div>
            <div className="brokerIA-stockCard__information">
                <div className="brokerIA-stockCard__data">
                    <div className="brokerIA-stockCard__prices">
                        <div>{`${formattedDate}, ${formattedTime} | ${formatAmount(
                            realTimeValue,
                            undefined,
                            4
                        )}\xa0${currency}`}</div>
                        <div>
                            <strong>
                                {`${formatText('brokerIA-stockCard_estimated')} | ${formatAmount(
                                    predictedValue,
                                    undefined,
                                    4
                                )}\xa0${currency}`}
                            </strong>
                        </div>
                    </div>
                    <PercentagePill percent={expectedValue} compact={compact} />
                </div>
                <div className="brokerIA-stockCard__buySell">
                    {sellLink ? (
                        <OKButton
                            modifier="ghost"
                            size={compact && 'compact'}
                            onClick={handleSellButton}
                        >
                            <div>
                                {formatText(
                                    compact
                                        ? 'broker-myInvestmentsSellLink'
                                        : 'broker-stocksLikeHeaderSellLabel'
                                )}
                            </div>
                        </OKButton>
                    ) : (
                        <div className="brokerIA-stockCard__buySellPlaceholder" />
                    )}
                    {marketable.toLowerCase() === YES ? (
                        <OKButton
                            modifier="secondary"
                            size={compact && 'compact'}
                            onClick={handleBuyButton}
                        >
                            <div>
                                {formatText(
                                    compact
                                        ? 'broker-myInvestmentsBuyLink'
                                        : 'broker-stocksLikeHeaderBuyLabel'
                                )}
                            </div>
                        </OKButton>
                    ) : (
                        <div className="brokerIA-stockCard__buySellPlaceholder" />
                    )}
                </div>
            </div>
        </div>
    );
};

StockCard.propTypes = {
    stockData: PropTypes.object.isRequired,
    compact: PropTypes.Boolean,
    history: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    sellLink: PropTypes.String,
};

export default withRouter(StockCard);
